import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { remitosApi } from '../../api/remitos'
import EstadoVigenteBadge from '../../components/EstadoVigenteBadge'
import { fmtFecha } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { useAuth } from '../../context/AuthContext'
import AccionesRemitoMovil from './AccionesRemitoMovil'
import { Seccion, Dato, Cargando, ErrorMsg, DocTag, LinkMapa, ViajesSeccion } from './comunes'

// Detalle móvil de un remito (T90 o T80). GET /remitos/{id}. Si está
// ingresado: imprimir + confirmar (pasa a logística) — AccionesRemitoMovil.
export default function MovilRemitoDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [remito, setRemito] = useState(null)
  const [error, setError] = useState(null)
  const { rolOperativoVentas } = useAuth()

  const cargar = () => remitosApi.obtener(id)
    .then(({ data }) => setRemito(data))
    .catch(() => setError('No se pudo cargar el remito.'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar() }, [id])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!remito) return <Cargando />

  const esT80 = Number(remito.transporte) === 80
  const nv = remito.nota_venta || remito.notaVenta
  const dEnv = nv?.direccion_envio || remito.direccion_envio
  const direccion = dEnv?.retira
    ? null
    : dEnv && (dEnv.calle || dEnv.localidad)
      ? [[dEnv.calle, dEnv.piso].filter(Boolean).join(' '), dEnv.localidad, dEnv.provincia, dEnv.codigo_postal].filter(Boolean).join(', ')
      : [remito.cliente?.domicilio, remito.cliente?.localidad, remito.cliente?.provincia].filter(Boolean).join(', ')

  return (
    <div>
      <div style={{ margin: '4px 0 2px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <DocTag tipo="remito" numero={remito.numero_presea} />
        <span style={{
          padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
          background: esT80 ? colors.t80Bg : colors.t90Bg, color: esT80 ? colors.t80Text : colors.t90Text,
        }}>
          T{remito.transporte}
        </span>
        <span style={{ flex: 1 }} />
        {remito.estado_vigente
          ? <EstadoVigenteBadge estadoVigente={remito.estado_vigente} />
          : <span style={estadoRaw}>{remito.estado}</span>}
      </div>

      {rolOperativoVentas() && <AccionesRemitoMovil remito={remito} onCambio={cargar} />}

      <Seccion titulo="Cliente">
        <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.text, marginBottom: 4 }}>
          {remito.cliente?.nombre || '—'}
        </div>
        <Dato label="Código" mono>{remito.cliente?.codigo_presea || '—'}</Dato>
        <Dato label="Fecha emisión" mono>{fmtFecha(remito.fecha_emision)}</Dato>
        {remito.zona && <Dato label="Zona">{remito.zona.nombre}</Dato>}
        {nv?.numero_lgr && (
          <Dato label="Nota de venta">
            <button onClick={() => navigate(`/m/ventas/${nv.id}`)} style={linkBtn}>{nv.numero_lgr} →</button>
          </Dato>
        )}
      </Seccion>

      {!esT80 && (
        <Seccion titulo="Entrega">
          {dEnv?.retira ? (
            <p style={nota}>🏬 Retira el cliente{dEnv.sucursal ? ` — sucursal ${dEnv.sucursal}` : ' en depósito'}.</p>
          ) : (
            <>
              <Dato label="Dirección">{direccion || '—'}</Dato>
              <LinkMapa dir={direccion} />
            </>
          )}
        </Seccion>
      )}

      <Seccion titulo={`Artículos (${remito.articulos?.length || 0})`}>
        {(remito.articulos || []).map((a, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: i < remito.articulos.length - 1 ? `1px solid ${colors.borderSoft}` : 'none' }}>
            <span style={{ fontSize: text.body, color: colors.text, fontFamily: font.sans, minWidth: 0 }}>
              {(a.articulo?.factura_como || a.articulo)?.descripcion || '—'}
            </span>
            <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700, color: colors.text, flexShrink: 0 }}>
              {Number(a.cantidad)}
            </span>
          </div>
        ))}
      </Seccion>

      {/* Viajes del remito (T90): con qué salida salió y cómo terminó */}
      {!esT80 && (
        <ViajesSeccion
          docs={remito.salida_documentos || remito.salidaDocumentos}
          onVerSalida={(sid) => navigate(`/m/reparto/${sid}`)}
        />
      )}

      {esT80 && (remito.notas_envio || remito.notasEnvio || []).length > 0 && (
        <Seccion titulo={`Notas de envío (${(remito.notas_envio || remito.notasEnvio).length})`}>
          {(remito.notas_envio || remito.notasEnvio).map((ne, i, arr) => (
            <div key={ne.id} onClick={() => navigate(`/m/logistica/ne/${ne.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: i < arr.length - 1 ? `1px solid ${colors.borderSoft}` : 'none' }}>
              <DocTag tipo="ne" numero={ne.numero_ne} />
              <span style={{ fontSize: text.body, color: colors.text, fontFamily: font.sans, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ne.destinatario?.nombre || '—'}
              </span>
              <span style={{ color: colors.textMeta, fontSize: 16 }}>›</span>
            </div>
          ))}
        </Seccion>
      )}
    </div>
  )
}

const estadoRaw = {
  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
  background: colors.borderSoft, color: colors.textSec,
}
const nota = { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec, fontFamily: font.sans, lineHeight: 1.45 }
const linkBtn = {
  border: 'none', background: 'transparent', color: colors.link, fontFamily: font.mono,
  fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0,
}
